import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { Title } from '../components/ui/Title';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { Calendar, MapPin, Users, CreditCard as Edit, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { ActionMenu } from '../components/ui/ActionMenu';
import { useAuth } from '../contexts/AuthContext';
import { useProfile } from '../hooks/useProfile';
import { ConfirmationDialog } from '../components/ui/ConfirmationDialog';
import { EventMap } from '../components/EventMap';
import { FavoriteEventButton } from '../components/FavoriteEventButton';
import { SEOHead } from '../components/SEOHead';
import { dateLocales } from '../i18n';
import type { Event, Band, Profile } from '../types/database';

type EventWithDetails = Event & {
  band: Band & {
    members: {
      profile: Profile;
      role: string;
      instruments: string[];
    }[];
  };
  creator: Profile;
};

function EventDetails() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { bandSlug, eventSlug } = useParams<{ bandSlug: string; eventSlug: string }>();
  const { user } = useAuth();
  const { profile } = useProfile();
  const [event, setEvent] = useState<EventWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const locale = dateLocales[i18n.language as keyof typeof dateLocales] || dateLocales.en;

  useEffect(() => {
    async function fetchEventDetails() {
      try {
        if (!bandSlug || !eventSlug) {
          setError('Invalid URL: Missing band or event information');
          setTimeout(() => {
            navigate('/bands');
          }, 3000);
          setLoading(false);
          return;
        }

        const { data: bandData, error: bandError } = await supabase
          .from('bands')
          .select('id')
          .eq('slug', bandSlug)
          .maybeSingle();

        if (bandError) throw bandError;
        if (!bandData) {
          throw new Error(`Band "${bandSlug}" not found`);
        }

        const { data: eventData, error: eventError } = await supabase
          .from('events')
          .select(`
            *,
            creator:profiles!events_created_by_fkey(*)
          `)
          .eq('slug', eventSlug)
          .maybeSingle();

        if (eventError) throw eventError;
        if (!eventData) {
          throw new Error(`Event "${eventSlug}" not found`);
        }

        const { data: eventBands } = await supabase
          .from('event_bands')
          .select(`
            band:bands(
              *,
              members:band_members(
                profile:profiles(*),
                role,
                instruments
              )
            )
          `)
          .eq('event_id', eventData.id)
          .eq('band_id', bandData.id)
          .maybeSingle();

        if (!eventBands?.band) {
          throw new Error(`Event "${eventSlug}" not found for band "${bandSlug}"`);
        }

        setEvent({
          ...eventData,
          band: eventBands.band
        } as EventWithDetails);

        if (profile) {
          const { data: memberData } = await supabase
            .from('band_members')
            .select('role')
            .eq('band_id', bandData.id)
            .eq('profile_id', profile.id)
            .maybeSingle();

          setIsAdmin(memberData?.role === 'admin' || eventData.created_by === profile.id);
        }
      } catch (err) {
        console.error('Error fetching event details:', err);
        const errorMessage = err instanceof Error ? err.message : 'Failed to load event details';
        setError(errorMessage);
        
        if (errorMessage.includes('not found') || errorMessage.includes('Invalid URL')) {
          setTimeout(() => {
            navigate('/bands');
          }, 3000);
        }
      } finally {
        setLoading(false);
      }
    }

    fetchEventDetails();
  }, [bandSlug, eventSlug, profile, navigate]);

  const handleDelete = async () => {
    if (!event) return;

    try {
      setIsDeleting(true);
      const { error: deleteError } = await supabase
        .from('events')
        .delete()
        .eq('id', event.id);

      if (deleteError) throw deleteError;
      navigate(`/bands/${bandSlug}`);
    } catch (err) {
      console.error('Error deleting event:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete event');
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center text-light/60">Loading event details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center">
          <div className="text-red-500 mb-4">{error}</div>
          <div className="text-light/60">Redirecting to bands page...</div>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="container mx-auto px-6 py-24">
        <div className="text-center text-red-500">Event not found</div>
      </div>
    );
  }

  const breadcrumbItems = [
    { label: 'Bands', href: '/bands' },
    { label: event.band.name, href: `/bands/${bandSlug}` },
    { label: event.title },
  ];

  const eventActions = isAdmin ? [
    {
      label: 'Edit Event',
      icon: <Edit className="w-4 h-4" />,
      onClick: () => navigate(`/bands/${bandSlug}/events/${eventSlug}/edit`),
    },
    {
      label: 'Delete Event',
      icon: <Trash2 className="w-4 h-4" />,
      onClick: () => setShowDeleteConfirm(true),
      className: 'text-red-500 hover:text-red-500',
    }
  ] : [];

  // Generate structured data for the event
  const eventDate = new Date(event.event_date);
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "MusicEvent",
    "name": event.title,
    "description": event.description || `${event.band.name} live in ${event.location}`,
    "startDate": event.event_date,
    "endDate": new Date(eventDate.getTime() + 3 * 60 * 60 * 1000).toISOString(), // Assume 3 hours duration
    "location": {
      "@type": "Place",
      "name": event.location,
      "address": {
        "@type": "PostalAddress",
        "addressLocality": event.location
      },
      ...(event.coordinates && {
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": event.coordinates.latitude,
          "longitude": event.coordinates.longitude
        }
      })
    },
    "performer": {
      "@type": "MusicGroup",
      "name": event.band.name,
      "url": `https://jamnation.ch/bands/${bandSlug}`
    },
    "organizer": {
      "@type": "Person",
      "name": event.creator.artist_name || event.creator.full_name
    },
    "offers": {
      "@type": "Offer",
      "url": `https://jamnation.ch/bands/${bandSlug}/events/${eventSlug}`,
      "availability": "https://schema.org/InStock"
    },
    "image": event.band.avatar_url || "https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=1200&h=630&fit=crop"
  };

  return (
    <>
      <SEOHead
        title={`${event.title} | ${event.band.name} live in ${event.location}`}
        description={`${event.band.name} live in ${event.location} am ${format(new Date(event.event_date), 'dd.MM.yyyy', { locale })}. ${event.description || 'Erlebe Live-Musik in deiner Nähe.'}`}
        keywords={[
          event.title,
          event.band.name,
          'Konzert',
          'Live-Musik',
          event.location,
          'Musikveranstaltung',
          'Live-Event',
          ...(event.band.genres || [])
        ]}
        url={`https://jamnation.ch/bands/${bandSlug}/events/${eventSlug}`}
        image={event.band.avatar_url}
        type="music.event"
        structuredData={structuredData}
      />

      <div className="container mx-auto px-6 pb-8 md:py-24">
        <div className="mb-6">
          <Breadcrumbs items={breadcrumbItems} />
        </div>

       <motion.div 
        className="max-w-4xl mx-auto space-y-8"
        initial={{ opacity: 0, x: 200 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        {/* Event Details */}
        <div className="panel relative">
          {isAdmin && (
            <div className="absolute top-4 right-4 z-10">
              <ActionMenu items={eventActions} />
            </div>
          )}

          <div className="flex flex-col md:flex-row gap-6">
            <div className="md:w-50">
              <div className="bg-primary rounded-lg p-6 text-center">
                <div className="font-tilt-warp text-4xl text-light">
                  {format(new Date(event.event_date), 'dd. MMM yy', { locale })}
                </div>
                <div className="text-xl text-light">
                  {format(new Date(event.event_date), 'EE, HH:mm', { locale })} Uhr
                </div>
              </div>
            </div>

            <div className="md:w-auto flex justify-center flex-col">
              <div className="flex items-center gap-3">
                <h1 className="text-3xl font-bold text-light">{event.title}</h1>
                {user && profile && (
                  <FavoriteEventButton eventId={event.id} />
                )}
              </div>

              <div className="flex flex-col gap-2 mt-2">
                <div className="flex items-center text-light/60">
                  <MapPin className="w-5 h-5 mr-2 flex-shrink-0" />
                  <span>{event.location}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

       <div className="grid md:grid-cols-3 gap-8 items-stretch overflow-hidden">
          {/* Content Section - 2/3 width */}
          <div className="md:col-span-2">
            {/* Event Description Section */}
            {event.description && (
              <div className="mb-8">
                <div>
                  <div className="border-l border-solid border-primary pl-6 text-light/60 whitespace-pre-line">{event.description}</div>
                </div>
              </div>
            )}
            
            {/* Band Section */}
            <div className="space-y-6">
              <h2 className="text-2xl font-semibold">Band</h2>
              <Link
                to={`/bands/${bandSlug}`}
                className="block panel hover:bg-gray-custom/20"
              >
                <div className="flex items-center space-x-6">
                  <div className="w-32 h-32 bg-gray-custom/10 flex items-center justify-center overflow-hidden">
                    {event.band.avatar_url ? (
                      <img
                        src={event.band.avatar_url}
                        alt={event.band.name}
                        className="w-full h-full object-cover"
                       loading="lazy" />
                    ) : (
                      <Users className="w-8 h-8 text-light/60" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-light mb-2">{event.band.name}</h3>
                    <div className="text-sm text-light/60">
                      {event.band.location && (
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          <span>{event.band.location}</span>
                        </div>
                      )}
                    </div>
                    {event.band.genres && event.band.genres.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3">
                        {event.band.genres.map((genre) => (
                          <span
                            key={genre}
                            className="px-2 py-0.5 text-xs bg-tertiary rounded-full text-light"
                          >
                            {genre}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            </div>
          </div>
         <div className="md:col-span-1">
            {/* Location Section */}
            {event.coordinates && (
              <div>
                <h2 className="text-2xl font-semibold mb-6">Location</h2>
                <div className="hover:bg-gray-custom/20">
                  <EventMap coordinates={event.coordinates} />
                </div>
              </div>
            )}
         </div>
       </div>
      </motion.div>

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Delete Event"
        message={`Are you sure you want to delete "${event.title}"? This action cannot be undone.`}
        confirmLabel="Delete Event"
        isDanger={true}
        isLoading={isDeleting}
      />
    </div>
    </>
  );
}

export default EventDetails;